import { useQuery } from '@apollo/client';
import { GET_ACCREDITATION_APPLICATIONS } from 'graphql/member';
import { useCallback, useEffect, useState } from 'react';
import { IAccreditationApplication } from 'interfaces/IAccreditationApplication';
import { useAccreditationApplication } from './useAccreditationApplication';
import { useAccreditationTypes } from './useAccreditationTypes';

export const useAccreditationApplications = () => {
    // Queries
    const {
        data: appsData,
        loading: appsLoading,
        error: appsError,
        refetch: refetchApplications,
    } = useQuery(GET_ACCREDITATION_APPLICATIONS, {
        fetchPolicy: 'no-cache',
    });

    const {
        operations: { deleteApplication, submitApplication, upsertApplication },
        models: { error: appError, updating },
    } = useAccreditationApplication();

    const {
        accreditationTypes,
        loading: configLoading,
        error: configError,
    } = useAccreditationTypes();

    // State
    const [applications, setApplications] = useState<IAccreditationApplication[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        setLoading(configLoading || appsLoading);
    }, [configLoading, appsLoading]);

    useEffect(() => {
        if (appsError || configError || appError) {
            setError(appsError || configError || appError);
        }
    }, [appsError, configError, appError]);

    // Process application data when it loads
    useEffect(() => {
        if (!appsLoading && !appsError && appsData?.accreditationApplications) {
            setApplications(appsData.accreditationApplications);
        }

        if (appsError) {
            setError(appsError);
            console.error('Error loading applications:', appsError);
        }

        updateLoadingState();
    }, [appsLoading, appsError, appsData]);

    // Helper to update loading state based on all data loading states
    const updateLoadingState = useCallback(() => {
        if (!appsLoading && !configLoading) {
            setLoading(false);
        }
    }, [appsLoading, configLoading]);

    // Fetch updated applications data
    const fetchApplications = async (): Promise<IAccreditationApplication[]> => {
        setLoading(true);
        try {
            const { data } = await refetchApplications({ cachePolicy: 'no-cache' });
            const fetchedApps = data.accreditationApplications;
            setApplications(fetchedApps);
            return fetchedApps;
        } catch (error) {
            console.error('Error fetching applications:', error);
            setError(error instanceof Error ? error : new Error(String(error)));
            throw error;
        } finally {
            setLoading(false);
        }
    };

    return {
        models: {
            accreditationTypes,
            applications,
            error,
            loading,
            updating,
        },
        operations: {
            deleteApplication,
            fetchApplications,
            submitApplication,
            upsertApplication,
        },
    };
};
